// PBP Guinée — Sidebar navigation gouvernementale
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAppSelector } from '../../store';
import { clsx } from 'clsx';

interface NavItem {
  path: string;
  label: string;
  icon: string;
  roles?: string[];
  badge?: number;
}

const NAV_ITEMS: NavItem[] = [
  { path: '/dashboard',    label: 'Tableau de bord',    icon: '📊' },
  { path: '/patrimoine',   label: 'Registre National',  icon: '🏛️' },
  { path: '/cartographie', label: 'Cartographie SIG',   icon: '🗺️' },
  { path: '/recensement',  label: 'Recensement',        icon: '📋', roles: ['super_admin','dg_pbp','gestionnaire_immobilier','gestionnaire_regional','agent_recenseur'] },
  { path: '/occupations',  label: 'Occupations',        icon: '🏠' },
  { path: '/baux',         label: 'Baux & Loyers',      icon: '📜' },
  { path: '/maintenance',  label: 'Maintenance',         icon: '🔧' },
  { path: '/juridique',    label: 'Juridique',           icon: '⚖️', roles: ['super_admin','dg_pbp','service_juridique','gestionnaire_immobilier'] },
  { path: '/documents',    label: 'Documents GED',       icon: '📁' },
  { path: '/reporting',    label: 'Reporting',           icon: '📈' },
];

const ADMIN_ITEMS: NavItem[] = [
  { path: '/admin/utilisateurs', label: 'Utilisateurs', icon: '👥', roles: ['super_admin','dg_pbp'] },
];

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

export default function Sidebar({ isOpen }: SidebarProps) {
  const location = useLocation();
  const { user } = useAppSelector((state) => state.auth);

  const canAccess = (item: NavItem) => {
    if (!item.roles) return true;
    return user && item.roles.includes(user.role);
  };

  return (
    <aside
      className={clsx(
        'fixed top-0 left-0 h-full bg-sidebar-bg text-white z-50 transition-all duration-300 flex flex-col',
        isOpen ? 'w-64' : 'w-16'
      )}
    >
      {/* Logo PBP */}
      <div className="flex items-center h-20 px-4 border-b border-gray-700">
        <div className="flex items-center overflow-hidden">
          {isOpen ? (
            <img
              src="/logo.png"
              alt="PBP Guinée"
              className="h-14 w-auto object-contain"
            />
          ) : (
            <img
              src="/logo.png"
              alt="PBP"
              className="h-10 w-10 object-contain object-left"
            />
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-2">
        <div className="space-y-1">
          {NAV_ITEMS.filter(canAccess).map((item) => {
            const isActive = location.pathname === item.path ||
              (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
            return (
              <Link
                key={item.path}
                to={item.path}
                className={clsx(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-150',
                  isActive
                    ? 'bg-primary-600 text-white shadow-lg'
                    : 'text-gray-400 hover:bg-sidebar-hover hover:text-white'
                )}
                title={!isOpen ? item.label : undefined}
              >
                <span className="text-lg flex-shrink-0">{item.icon}</span>
                {isOpen && <span className="truncate font-medium">{item.label}</span>}
                {isOpen && item.badge && (
                  <span className="ml-auto bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center">
                    {item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </div>

        {/* Section Administration */}
        {ADMIN_ITEMS.some(canAccess) && (
          <div className="mt-6">
            {isOpen && (
              <p className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Administration
              </p>
            )}
            {ADMIN_ITEMS.filter(canAccess).map((item) => {
              const isActive = location.pathname.startsWith(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={clsx(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-150',
                    isActive
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-400 hover:bg-sidebar-hover hover:text-white'
                  )}
                >
                  <span className="text-lg flex-shrink-0">{item.icon}</span>
                  {isOpen && <span className="truncate font-medium">{item.label}</span>}
                </Link>
              );
            })}
          </div>
        )}
      </nav>

      {/* Profil utilisateur */}
      {user && (
        <div className="border-t border-gray-700 p-3">
          <Link to="/profil" className="flex items-center gap-3 hover:bg-sidebar-hover rounded-lg p-2 transition-colors">
            <div className="w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center flex-shrink-0 text-xs font-bold">
              {user.prenom?.[0]}{user.nom?.[0]}
            </div>
            {isOpen && (
              <div className="overflow-hidden">
                <p className="text-sm font-medium text-white truncate">{user.nom_complet}</p>
                <p className="text-xs text-gray-400 truncate">{user.role?.replace(/_/g, ' ')}</p>
              </div>
            )}
          </Link>
        </div>
      )}
    </aside>
  );
}
